import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '@/lib/api';
import { useAuthStore } from '@/hooks/useAuthStore';

interface Setting {
  id: string;
  key: string;
  value: string;
  encrypted: boolean;
  category: string;
  label: string | null;
  hasValue?: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

interface SmtpFormData {
  smtp_host: string;
  smtp_port: string;
  smtp_secure: string;
  smtp_user: string;
  smtp_pass: string;
  smtp_from: string;
}

// C26 PR-2 (tri-audit, Eje 2 de AI-CHAT-INVESTIGATION-2026-07-31.md §2):
// mismo patron SystemConfig que SMTP arriba, category='ai'. Backend en
// apps/api/src/config/ai.ts (PR-1), endpoint generico PATCH /settings/:key
// sin cambios (ya sirve cualquier category/key).
interface AiFormData {
  anthropic_api_key: string;
  anthropic_chat_model: string;
}

// C35 PR-3 (W4, spec MONEY-INFLOW-2026-08-23 §W4): tasas de referencia y
// umbrales del motor del excedente. MISMO patron SystemConfig que SMTP e IA,
// category='finanzas'. Backend: apps/api/src/config/finanzas.ts.
//
// Por que se editan aqui y no son constantes: CETES cambia cada semana, Banxico
// cada seis, la inflacion cada mes — y estos numeros gobiernan el umbral de
// "deuda cara" y el neto/real que la app le muestra al usuario. Un numero de
// mercado hardcodeado nace correcto y envejece mintiendo (leccion A-series C33).
// Cero llamadas de red: no hay API de mercado, por eso existe la fecha de
// referencia (el copy dice de cuando son las tasas en vez de fingir vivo).
interface FinanzasFormData {
  finanzas_cetes_bruto_pct: string;
  finanzas_retencion_isr_pct: string;
  finanzas_inflacion_anual_pct: string;
  finanzas_tasa_banxico_pct: string;
  finanzas_colchon_minimo_meses: string;
  finanzas_colchon_objetivo_meses: string;
  finanzas_margen_deuda_cara_pp: string;
  finanzas_referencia_actualizada: string;
}

/**
 * Etiquetas, ayuda y rangos SANOS — los MISMOS que valida el backend
 * (`RANGOS` en apps/api/src/config/finanzas.ts). Front y back alineados a
 * proposito (restriccion 5 del ciclo, leccion A-series de C33): si el admin
 * escribe 400 aqui, el input lo marca; si aun asi llegara, el backend lo
 * registra y usa el default. Ninguna de las dos capas confia en la otra.
 */
const CAMPOS_FINANZAS: Array<{
  key: keyof FinanzasFormData;
  label: string;
  ayuda: string;
  tipo: 'number' | 'date';
  min?: number;
  max?: number;
  step?: string;
}> = [
  { key: 'finanzas_cetes_bruto_pct', label: 'CETES 28d — bruto (%)', ayuda: 'Rendimiento anual bruto de la ultima subasta', tipo: 'number', min: 0, max: 100, step: '0.01' },
  { key: 'finanzas_retencion_isr_pct', label: 'Retencion ISR (% anual)', ayuda: 'Retencion provisional sobre el CAPITAL (2026: 0.90)', tipo: 'number', min: 0, max: 100, step: '0.01' },
  { key: 'finanzas_inflacion_anual_pct', label: 'Inflacion anual esperada (%)', ayuda: 'Se resta al neto para publicar el rendimiento REAL', tipo: 'number', min: 0, max: 100, step: '0.01' },
  { key: 'finanzas_tasa_banxico_pct', label: 'Tasa objetivo Banxico (%)', ayuda: 'Informativa: contexto de las tasas del mercado', tipo: 'number', min: 0, max: 100, step: '0.01' },
  { key: 'finanzas_colchon_minimo_meses', label: 'Colchon minimo (meses)', ayuda: 'Meses de gastos fijos del paso 1 de la cascada', tipo: 'number', min: 0.5, max: 24, step: '0.5' },
  { key: 'finanzas_colchon_objetivo_meses', label: 'Colchon objetivo (meses)', ayuda: 'Meta del paso 3. Debe ser >= al minimo', tipo: 'number', min: 0.5, max: 24, step: '0.5' },
  { key: 'finanzas_margen_deuda_cara_pp', label: 'Margen deuda cara (pp)', ayuda: 'Umbral = CETES neto + margen. Con los valores base: 15.25 %', tipo: 'number', min: 0, max: 50, step: '0.25' },
  { key: 'finanzas_referencia_actualizada', label: 'Referencias actualizadas al', ayuda: 'Fecha que la app publica junto a las tasas', tipo: 'date' },
];

interface ApiResponse {
  success: boolean;
  data?: {
    settings?: Setting[];
    message?: string;
  };
}

export default function Settings() {
  const queryClient = useQueryClient();
  const { staff } = useAuthStore();
  const isSuperAdmin = staff?.role === 'SUPER_ADMIN';

  const [formData, setFormData] = useState<SmtpFormData>({
    smtp_host: '',
    smtp_port: '587',
    smtp_secure: 'false',
    smtp_user: '',
    smtp_pass: '',
    smtp_from: 'SolidCycle <noreply@solidcycle.app>',
  });
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // C26 PR-2 — Configuracion IA (Anthropic): mismo patron de estado que SMTP
  // arriba, formulario/mutacion propios (banners independientes).
  const [aiFormData, setAiFormData] = useState<AiFormData>({
    anthropic_api_key: '',
    anthropic_chat_model: '',
  });
  const [aiSaveSuccess, setAiSaveSuccess] = useState(false);

  // C35 PR-3 — Tasas de referencia (category='finanzas'). Tercer formulario
  // independiente, mismo patron que los dos de arriba.
  const [finanzasFormData, setFinanzasFormData] = useState<FinanzasFormData>({
    finanzas_cetes_bruto_pct: '',
    finanzas_retencion_isr_pct: '',
    finanzas_inflacion_anual_pct: '',
    finanzas_tasa_banxico_pct: '',
    finanzas_colchon_minimo_meses: '',
    finanzas_colchon_objetivo_meses: '',
    finanzas_margen_deuda_cara_pp: '',
    finanzas_referencia_actualizada: '',
  });
  const [finanzasSaveSuccess, setFinanzasSaveSuccess] = useState(false);

  // Fetch SMTP settings
  const { data, isLoading, error } = useQuery<ApiResponse>({
    queryKey: ['settings', 'smtp'],
    queryFn: () => settingsApi.getByCategory('smtp'),
  });

  // Fetch AI (Anthropic) settings — segundo hook independiente, no interfiere
  // con el de SMTP (misma categoria generica de settings, distinta 'category').
  const { data: aiData, isLoading: aiIsLoading, error: aiError } = useQuery<ApiResponse>({
    queryKey: ['settings', 'ai'],
    queryFn: () => settingsApi.getByCategory('ai'),
  });

  // Effect to update form when data loads
  // Uses functional setState (prev) to avoid stale closure; formData is NOT added to deps
  // to prevent infinite loop (setFormData → formData change → effect re-run)
  useEffect(() => {
    if (data?.success && data?.data?.settings) {
      const settings = data.data.settings;
      setFormData((prev) => {
        const newFormData: SmtpFormData = { ...prev };
        settings.forEach((s) => {
          if (s.key in newFormData) {
            const key = s.key as keyof SmtpFormData;
            newFormData[key] = s.encrypted ? '' : s.value;
          }
        });
        return newFormData;
      });
    }
  }, [data]);

  // Mismo patron que el effect de SMTP arriba, para el formulario de IA.
  useEffect(() => {
    if (aiData?.success && aiData?.data?.settings) {
      const settings = aiData.data.settings;
      setAiFormData((prev) => {
        const newFormData: AiFormData = { ...prev };
        settings.forEach((s) => {
          if (s.key in newFormData) {
            const key = s.key as keyof AiFormData;
            newFormData[key] = s.encrypted ? '' : s.value;
          }
        });
        return newFormData;
      });
    }
  }, [aiData]);

  // C35 PR-3 — mismo patron de fetch/effect que SMTP e IA, category='finanzas'.
  const { data: finanzasData, isLoading: finanzasIsLoading, error: finanzasError } =
    useQuery<ApiResponse>({
      queryKey: ['settings', 'finanzas'],
      queryFn: () => settingsApi.getByCategory('finanzas'),
    });

  useEffect(() => {
    if (finanzasData?.success && finanzasData?.data?.settings) {
      const settings = finanzasData.data.settings;
      setFinanzasFormData((prev) => {
        const newFormData: FinanzasFormData = { ...prev };
        settings.forEach((s) => {
          if (s.key in newFormData) {
            const key = s.key as keyof FinanzasFormData;
            newFormData[key] = s.value;
          }
        });
        return newFormData;
      });
    }
  }, [finanzasData]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: settingsApi.updateMany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  // Update mutation (IA) — instancia separada de la de SMTP: banners de exito
  // independientes por tarjeta, mismo endpoint generico settingsApi.updateMany.
  const updateAiMutation = useMutation({
    mutationFn: settingsApi.updateMany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setAiSaveSuccess(true);
      setTimeout(() => setAiSaveSuccess(false), 3000);
    },
  });

  // Test SMTP mutation
  const testMutation = useMutation({
    mutationFn: settingsApi.testSmtp,
    onSuccess: (response) => {
      setTestResult(response);
      setTimeout(() => setTestResult(null), 5000);
    },
    onError: (error: Error) => {
      setTestResult({ success: false, message: error.message });
      setTimeout(() => setTestResult(null), 5000);
    },
  });

  const handleChange = (key: keyof SmtpFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleAiChange = (key: keyof AiFormData, value: string) => {
    setAiFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleAiSave = () => {
    const settings = Object.entries(aiFormData)
      .filter(([_, value]) => value !== '' && value !== '********')
      .map(([key, value]) => ({ key, value }));

    updateAiMutation.mutate(settings);
  };

  const updateFinanzasMutation = useMutation({
    mutationFn: settingsApi.updateMany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setFinanzasSaveSuccess(true);
      setTimeout(() => setFinanzasSaveSuccess(false), 3000);
    },
  });

  const handleFinanzasChange = (key: keyof FinanzasFormData, value: string) => {
    setFinanzasFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFinanzasSave = () => {
    const settings = Object.entries(finanzasFormData)
      .filter(([, value]) => value !== '')
      .map(([key, value]) => ({ key, value }));

    updateFinanzasMutation.mutate(settings);
  };

  const handleSave = () => {
    const settings = Object.entries(formData)
      .filter(([_, value]) => value !== '' && value !== '********')
      .map(([key, value]) => ({ key, value }));

    updateMutation.mutate(settings);
  };

  const handleTest = () => {
    testMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Error cargando configuracion
        </div>
      </div>
    );
  }

  const settings = data?.data?.settings as Setting[] || [];
  const aiSettings = aiData?.data?.settings as Setting[] || [];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Configuracion</h1>
        <p className="text-gray-600">Configura los parametros del sistema</p>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Configuracion SMTP</h2>
            <p className="text-sm text-gray-500">
              Configura el servidor de correo para envio de emails
            </p>
          </div>
          <button
            onClick={handleTest}
            disabled={testMutation.isPending || !formData.smtp_host}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            {testMutation.isPending ? 'Probando...' : 'Probar Conexion'}
          </button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div
            className={`mb-4 p-3 rounded-lg ${
              testResult.success
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {testResult.message}
          </div>
        )}

        {/* Save Success */}
        {saveSuccess && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 border border-green-200">
            Configuracion guardada correctamente
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {/* SMTP Host */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Servidor SMTP
            </label>
            <input
              type="text"
              value={formData.smtp_host}
              onChange={(e) => handleChange('smtp_host', e.target.value)}
              placeholder="smtp.example.com"
              disabled={!isSuperAdmin}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
            />
          </div>

          {/* SMTP Port */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Puerto
            </label>
            <input
              type="text"
              value={formData.smtp_port}
              onChange={(e) => handleChange('smtp_port', e.target.value)}
              placeholder="587"
              disabled={!isSuperAdmin}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
            />
          </div>

          {/* SMTP User */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Usuario
            </label>
            <input
              type="text"
              value={formData.smtp_user}
              onChange={(e) => handleChange('smtp_user', e.target.value)}
              placeholder="user@example.com"
              disabled={!isSuperAdmin}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
            />
          </div>

          {/* SMTP Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contrasena
              {settings.find((s) => s.key === 'smtp_pass')?.hasValue && (
                <span className="text-green-600 ml-2">(configurada)</span>
              )}
            </label>
            <input
              type="password"
              value={formData.smtp_pass}
              onChange={(e) => handleChange('smtp_pass', e.target.value)}
              placeholder="********"
              disabled={!isSuperAdmin}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
            />
          </div>

          {/* SMTP Secure */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TLS/SSL
            </label>
            <select
              value={formData.smtp_secure}
              onChange={(e) => handleChange('smtp_secure', e.target.value)}
              disabled={!isSuperAdmin}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
            >
              <option value="false">STARTTLS (puerto 587)</option>
              <option value="true">SSL/TLS (puerto 465)</option>
            </select>
          </div>

          {/* SMTP From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Direccion de envio
            </label>
            <input
              type="text"
              value={formData.smtp_from}
              onChange={(e) => handleChange('smtp_from', e.target.value)}
              placeholder="SolidCycle <noreply@solidcycle.app>"
              disabled={!isSuperAdmin}
              className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
            />
          </div>
        </div>

        {/* Save Button */}
        {isSuperAdmin && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        )}
      </div>

      {/* Configuracion IA (Anthropic) — C26 PR-2, Eje 2 de
          AI-CHAT-INVESTIGATION-2026-07-31.md §2. Mismo patron/tarjeta que
          SMTP arriba (category='ai' en system_config). Deliberadamente SIN
          boton "Probar conexion": a diferencia de SMTP, probar la key de
          Anthropic implica una llamada real de red que consume saldo
          facturable de la cuenta (PH-19 sigue abierto — saldo agotado). */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Configuracion IA (Anthropic)</h2>
          <p className="text-sm text-gray-500">
            Configura la API key y el modelo del asistente de chat financiero
          </p>
        </div>

        {aiIsLoading && (
          <div className="mb-4 flex justify-center">
            <div className="animate-spin h-6 w-6 border-4 border-primary-500 border-t-transparent rounded-full"></div>
          </div>
        )}

        {!aiIsLoading && aiError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
            Error cargando configuracion de IA
          </div>
        )}

        {/* Save Success */}
        {aiSaveSuccess && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 border border-green-200">
            Configuracion guardada correctamente
          </div>
        )}

        {!aiIsLoading && !aiError && (
          <div className="grid grid-cols-2 gap-4">
            {/* Anthropic API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key de Anthropic
                {aiSettings.find((s) => s.key === 'anthropic_api_key')?.hasValue && (
                  <span className="text-green-600 ml-2">(configurada)</span>
                )}
              </label>
              <input
                type="password"
                value={aiFormData.anthropic_api_key}
                onChange={(e) => handleAiChange('anthropic_api_key', e.target.value)}
                placeholder="********"
                disabled={!isSuperAdmin}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
              />
            </div>

            {/* Anthropic Chat Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Modelo de chat
              </label>
              <input
                type="text"
                value={aiFormData.anthropic_chat_model}
                onChange={(e) => handleAiChange('anthropic_chat_model', e.target.value)}
                placeholder="claude-sonnet-5"
                disabled={!isSuperAdmin}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
              />
            </div>
          </div>
        )}

        {/* Save Button */}
        {isSuperAdmin && !aiIsLoading && !aiError && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleAiSave}
              disabled={updateAiMutation.isPending}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {updateAiMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        )}
      </div>

      {/* Tasas de referencia (C35 PR-3, W4). Mismo patron/tarjeta que SMTP e IA
          (category='finanzas'). Sin boton "Probar": no hay nada que probar
          contra la red — estas tasas se capturan a mano precisamente porque el
          producto no consume ninguna API de mercado. */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Tasas de referencia</h2>
          <p className="text-sm text-gray-500">
            Referencias de mercado y umbrales que usa el asignador de excedente, el
            indicador de colchon y el rendimiento neto/real de inversiones
          </p>
        </div>

        {finanzasIsLoading && (
          <div className="mb-4 flex justify-center">
            <div className="animate-spin h-6 w-6 border-4 border-primary-500 border-t-transparent rounded-full"></div>
          </div>
        )}

        {!finanzasIsLoading && finanzasError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
            Error cargando tasas de referencia
          </div>
        )}

        {finanzasSaveSuccess && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 border border-green-200">
            Tasas de referencia guardadas correctamente
          </div>
        )}

        {!finanzasIsLoading && !finanzasError && (
          <>
            <div className="grid grid-cols-2 gap-4">
              {CAMPOS_FINANZAS.map((campo) => (
                <div key={campo.key}>
                  <label htmlFor={campo.key} className="block text-sm font-medium text-gray-700 mb-1">
                    {campo.label}
                  </label>
                  <input
                    id={campo.key}
                    type={campo.tipo}
                    inputMode={campo.tipo === 'number' ? 'decimal' : undefined}
                    min={campo.min}
                    max={campo.max}
                    step={campo.step}
                    value={finanzasFormData[campo.key]}
                    onChange={(e) => handleFinanzasChange(campo.key, e.target.value)}
                    disabled={!isSuperAdmin}
                    className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">{campo.ayuda}</p>
                </div>
              ))}
            </div>

            <p className="mt-4 text-xs text-gray-500">
              Un valor fuera de rango o no numerico no rompe la app: el backend lo
              registra y usa el valor por omision documentado.
            </p>
          </>
        )}

        {isSuperAdmin && !finanzasIsLoading && !finanzasError && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleFinanzasSave}
              disabled={updateFinanzasMutation.isPending}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {updateFinanzasMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        )}
      </div>

      {/* Providers Help */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-blue-900 mb-3">
          Proveedores SMTP recomendados
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="bg-white p-3 rounded-lg">
            <p className="font-medium text-gray-900">Gmail</p>
            <p className="text-gray-600">smtp.gmail.com:587</p>
            <p className="text-xs text-gray-500 mt-1">Requiere App Password</p>
          </div>
          <div className="bg-white p-3 rounded-lg">
            <p className="font-medium text-gray-900">Mailgun</p>
            <p className="text-gray-600">smtp.mailgun.org:587</p>
            <p className="text-xs text-gray-500 mt-1">10,000/mes gratis</p>
          </div>
          <div className="bg-white p-3 rounded-lg">
            <p className="font-medium text-gray-900">SendGrid</p>
            <p className="text-gray-600">smtp.sendgrid.net:587</p>
            <p className="text-xs text-gray-500 mt-1">100/dia gratis</p>
          </div>
          <div className="bg-white p-3 rounded-lg">
            <p className="font-medium text-gray-900">Resend</p>
            <p className="text-gray-600">smtp.resend.com:587</p>
            <p className="text-xs text-gray-500 mt-1">100/dia gratis</p>
          </div>
        </div>
      </div>
    </div>
  );
}
